// WebAuthn Service
// Handles integration with Supabase for passkey operations

import { supabase } from './supabase';
import { WebAuthnUtils } from './webauthn-utils';
import type {
  WebAuthnCredential,
  WebAuthnAuditLog,
  PasskeyRegistrationRequest,
  PasskeyRegistrationResponse,
  PasskeyAuthenticationRequest,
  PasskeyAuthenticationResponse,
  VerificationResult,
  WebAuthnChallenge,
  WebAuthnError,
  PasskeyListItem
} from '../types/webauthn';

const RP_ID = window.location.hostname;
const RP_NAME = 'Saúde Virtual';
const ORIGIN = window.location.origin;

export class WebAuthnService {
  private static challenges = new Map<string, WebAuthnChallenge>();



  /**
   * Get user's passkey credentials
   */
  static async getUserCredentials(userId: string): Promise<PasskeyListItem[]> {
    try {
      const { data, error } = await supabase
        .from('webauthn_credentials')
        .select('*')
        .eq('user_id', userId)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) throw error;

      return data.map(cred => ({
        id: cred.id,
        credential_id: cred.credential_id,
        name: cred.credential_name || 'Passkey sem nome',
        created_at: cred.created_at,
        last_used_at: cred.last_used_at,
        device_type: cred.device_type,
        transports: cred.transports || [],
        is_active: cred.is_active
      }));
    } catch (error) {
      console.error('Error fetching user credentials:', error);
      throw WebAuthnUtils.WebAuthnErrorHandler.handleError(error);
    }
  }

  /**
   * Start passkey registration process
   */
  static async startRegistration(request: PasskeyRegistrationRequest): Promise<PublicKeyCredentialCreationOptions> {
    try {
      // Get existing credentials to exclude
      const existingCreds = await this.getUserCredentials(request.user_id);
      const excludeCredentials = existingCreds.map(cred => cred.credential_id);

      // Generate challenge
      const challenge = WebAuthnUtils.ChallengeManager.createChallenge(RP_ID, ORIGIN, request.user_id);
      this.challenges.set(challenge.challenge, challenge);

      // Build registration options
      const options = WebAuthnUtils.CredentialOptionsBuilder.buildRegistrationOptions(
        request,
        WebAuthnUtils.Base64URL.decode(challenge.challenge),
        RP_ID,
        RP_NAME,
        ORIGIN,
        excludeCredentials
      );

      // Log registration start
      await this.logAuditEvent({
        user_id: request.user_id,
        action: 'register',
        success: true,
        challenge: challenge.challenge,
        origin: ORIGIN,
        rp_id: RP_ID,
        metadata: { registration_request: request }
      });

      return options;
    } catch (error) {
      console.error('Error starting registration:', error);
      throw WebAuthnUtils.WebAuthnErrorHandler.handleError(error);
    }
  }

  /**
   * Complete passkey registration
   */
  static async completeRegistration(
    userId: string,
    credential: PublicKeyCredential,
    credentialName?: string
  ): Promise<WebAuthnCredential> {
    try {
      console.log('🔐 Iniciando completeRegistration para usuário:', userId);
      
      // Verificar se o usuário está autenticado
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) {
        console.error('❌ Erro ao verificar sessão:', sessionError);
        throw new Error('Erro de autenticação: ' + sessionError.message);
      }
      
      if (!session || !session.user) {
        console.error('❌ Usuário não autenticado');
        throw new Error('Usuário deve estar autenticado para registrar passkey');
      }
      
      if (session.user.id !== userId) {
        console.error('❌ ID do usuário não confere:', { sessionUserId: session.user.id, requestUserId: userId });
        throw new Error('ID do usuário não confere com a sessão atual');
      }
      
      console.log('✅ Usuário autenticado:', session.user.id);
      
      // Parse the credential response
      const parsedResponse = WebAuthnUtils.ResponseParser.parseRegistrationResponse(credential);
      console.log('✅ Credencial parseada:', { credentialId: parsedResponse.credential_id });
      
      // Validate the response
      const clientDataJSON = parsedResponse.client_data_json;
      const clientData = JSON.parse(WebAuthnUtils.Base64URL.toString(clientDataJSON.buffer));
      
      // Verify challenge
      const storedChallenge = this.challenges.get(clientData.challenge);
      if (!storedChallenge || !WebAuthnUtils.ChallengeManager.isValidChallenge(storedChallenge)) {
        throw new Error('Invalid or expired challenge');
      }

      // Verify origin
      if (!WebAuthnUtils.ValidationUtils.validateOrigin(clientDataJSON.buffer, ORIGIN)) {
        throw new Error('Invalid origin');
      }

      console.log('✅ Validações passaram, inserindo no banco...');
      
      // Store credential in database
      const credentialData = {
        user_id: userId,
        credential_id: parsedResponse.credential_id,
        public_key: parsedResponse.public_key,
        counter: 0,
        device_type: parsedResponse.backup_eligible ? 'cross_platform' : 'single_device',
        backup_eligible: parsedResponse.backup_eligible || false,
        backup_state: parsedResponse.backup_state || false,
        transports: parsedResponse.transports || [],
        authenticator_attachment: parsedResponse.authenticator_attachment,
        user_verification: true,
        credential_name: credentialName || 'Passkey',
        is_active: true,
        metadata: {
          registration_time: new Date().toISOString(),
          user_agent: WebAuthnUtils.WebAuthnDetector.getUserAgent()
        }
      };

      console.log('📤 Dados da credencial:', {
        user_id: credentialData.user_id,
        credential_id: credentialData.credential_id,
        credential_name: credentialData.credential_name
      });

      const { data, error } = await supabase
        .from('webauthn_credentials')
        .insert(credentialData)
        .select()
        .single();

      if (error) {
        console.error('❌ Erro na inserção:', error);
        console.error('📋 Detalhes do erro:', {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint
        });
        throw error;
      }
      
      console.log('✅ Credencial inserida com sucesso:', data.id);

      // Clean up challenge
      this.challenges.delete(clientData.challenge);

      // Log successful registration
      await this.logAuditEvent({
        user_id: userId,
        credential_id: parsedResponse.credential_id,
        action: 'register',
        success: true,
        challenge: clientData.challenge,
        origin: ORIGIN,
        rp_id: RP_ID,
        metadata: { credential_name: credentialName }
      });

      return data;
    } catch (error) {
      console.error('Error completing registration:', error);
      
      // Log failed registration
      await this.logAuditEvent({
        user_id: userId,
        action: 'failed_register',
        success: false,
        error_message: error.message,
        origin: ORIGIN,
        rp_id: RP_ID
      });

      throw WebAuthnUtils.WebAuthnErrorHandler.handleError(error);
    }
  }

  /**
   * Start passkey authentication process
   */
  static async startAuthentication(request?: PasskeyAuthenticationRequest): Promise<PublicKeyCredentialRequestOptions> {
    try {
      console.log('🔐 Iniciando autenticação com passkey...');
      
      // Generate challenge
      const challenge = WebAuthnUtils.ChallengeManager.createChallenge(RP_ID, ORIGIN, request?.user_id);
      this.challenges.set(challenge.challenge, challenge);

      // Para autenticação universal, não restringimos credenciais específicas
      // Isso permite que o navegador mostre todos os passkeys disponíveis
      let allowCredentials: string[] = [];
      
      // Só restringir credenciais se explicitamente solicitado
      if (request?.credential_ids && request.credential_ids.length > 0) {
        allowCredentials = request.credential_ids;
        console.log('🔍 Usando credenciais específicas:', allowCredentials.length);
      } else {
        console.log('🌐 Permitindo qualquer passkey disponível (autenticação universal)');
      }

      // Build authentication options
      const options = WebAuthnUtils.CredentialOptionsBuilder.buildAuthenticationOptions(
        request || {},
        WebAuthnUtils.Base64URL.decode(challenge.challenge),
        RP_ID,
        allowCredentials
      );

      console.log('✅ Opções de autenticação criadas com sucesso');
      return options;
    } catch (error) {
      console.error('❌ Erro ao iniciar autenticação:', error);
      throw WebAuthnUtils.WebAuthnErrorHandler.handleError(error);
    }
  }

  /**
   * Complete passkey authentication
   */
  static async completeAuthentication(
    credential: PublicKeyCredential
  ): Promise<{ user_id: string; credential: WebAuthnCredential }> {
    try {
      console.log('🔐 Iniciando completeAuthentication...');
      
      // Parse the credential response
      const parsedResponse = WebAuthnUtils.ResponseParser.parseAuthenticationResponse(credential);
      console.log('✅ Credencial parseada:', { credentialId: parsedResponse.credential_id });
      
      // Get stored credential
      console.log('🔍 Buscando credencial no banco:', parsedResponse.credential_id);
      const { data: storedCred, error: credError } = await supabase
        .from('webauthn_credentials')
        .select('*')
        .eq('credential_id', parsedResponse.credential_id)
        .eq('is_active', true)
        .single();

      if (credError) {
        console.error('❌ Erro ao buscar credencial:', credError);
        console.error('📋 Detalhes do erro:', {
          code: credError.code,
          message: credError.message,
          details: credError.details,
          hint: credError.hint
        });
        
        // Verificar se existem credenciais para debug
        console.log('🔍 Verificando todas as credenciais ativas...');
        const { data: allCreds, error: allError } = await supabase
          .from('webauthn_credentials')
          .select('credential_id, user_id, credential_name, created_at')
          .eq('is_active', true)
          .limit(10);
        
        if (allError) {
          console.error('❌ Erro ao buscar todas as credenciais:', allError);
        } else {
          console.log('📊 Credenciais ativas encontradas:', allCreds?.length || 0);
          if (allCreds && allCreds.length > 0) {
            allCreds.forEach((cred, index) => {
              console.log(`  ${index + 1}. ID: ${cred.credential_id} | User: ${cred.user_id} | Name: ${cred.credential_name}`);
            });
          }
        }
        
        throw new Error(`Credencial não encontrada: ${credError.message}`);
      }
      
      if (!storedCred) {
        console.error('❌ Credencial não encontrada ou inativa');
        throw new Error('Credencial não encontrada ou inativa');
      }
      
      console.log('✅ Credencial encontrada:', {
        id: storedCred.id,
        user_id: storedCred.user_id,
        credential_name: storedCred.credential_name
      });

      // Validate the response
      const clientDataJSON = parsedResponse.client_data_json;
      const clientData = JSON.parse(WebAuthnUtils.Base64URL.toString(clientDataJSON.buffer));
      
      // Verify challenge
      const storedChallenge = this.challenges.get(clientData.challenge);
      if (!storedChallenge || !WebAuthnUtils.ChallengeManager.isValidChallenge(storedChallenge)) {
        throw new Error('Invalid or expired challenge');
      }

      // Verify origin
      if (!WebAuthnUtils.ValidationUtils.validateOrigin(clientDataJSON.buffer, ORIGIN)) {
        throw new Error('Invalid origin');
      }

      // TODO: Implement signature verification
      // This would require a crypto library for ECDSA/RSA verification
      // For now, we'll trust the browser's validation

      // Update credential usage
      const newCounter = storedCred.counter + 1;
      await supabase
        .from('webauthn_credentials')
        .update({
          counter: newCounter,
          last_used_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', storedCred.id);

      // Clean up challenge
      this.challenges.delete(clientData.challenge);

      // Log successful authentication
      await this.logAuditEvent({
        user_id: storedCred.user_id,
        credential_id: parsedResponse.credential_id,
        action: 'authenticate',
        success: true,
        challenge: clientData.challenge,
        origin: ORIGIN,
        rp_id: RP_ID
      });

      return {
        user_id: storedCred.user_id,
        credential: storedCred
      };
    } catch (error) {
      console.error('Error completing authentication:', error);
      
      // Log failed authentication
      await this.logAuditEvent({
        credential_id: WebAuthnUtils.Base64URL.encode(credential.rawId),
        action: 'failed_auth',
        success: false,
        error_message: error.message,
        origin: ORIGIN,
        rp_id: RP_ID
      });

      throw WebAuthnUtils.WebAuthnErrorHandler.handleError(error);
    }
  }

  /**
   * Delete a passkey credential
   */
  static async deleteCredential(credentialId: string, userId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('webauthn_credentials')
        .update({ is_active: false })
        .eq('credential_id', credentialId)
        .eq('user_id', userId);

      if (error) throw error;

      // Log credential deletion
      await this.logAuditEvent({
        user_id: userId,
        credential_id: credentialId,
        action: 'delete',
        success: true,
        origin: ORIGIN,
        rp_id: RP_ID
      });

      return true;
    } catch (error) {
      console.error('Error deleting credential:', error);
      throw WebAuthnUtils.WebAuthnErrorHandler.handleError(error);
    }
  }

  /**
   * Update credential name
   */
  static async updateCredentialName(
    credentialId: string,
    userId: string,
    newName: string
  ): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('webauthn_credentials')
        .update({ 
          credential_name: newName,
          updated_at: new Date().toISOString()
        })
        .eq('credential_id', credentialId)
        .eq('user_id', userId);

      if (error) throw error;

      // Log credential update
      await this.logAuditEvent({
        user_id: userId,
        credential_id: credentialId,
        action: 'update',
        success: true,
        origin: ORIGIN,
        rp_id: RP_ID,
        metadata: { new_name: newName }
      });

      return true;
    } catch (error) {
      console.error('Error updating credential:', error);
      throw WebAuthnUtils.WebAuthnErrorHandler.handleError(error);
    }
  }

  /**
   * Get user's audit logs
   */
  static async getUserAuditLogs(userId: string, limit = 50): Promise<WebAuthnAuditLog[]> {
    try {
      const { data, error } = await supabase
        .from('webauthn_audit_logs')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      throw WebAuthnUtils.WebAuthnErrorHandler.handleError(error);
    }
  }

  /**
   * Log audit event
   */
  private static async logAuditEvent(event: Partial<WebAuthnAuditLog>): Promise<void> {
    try {
      const auditData = {
        ...event,
        ip_address: await this.getClientIP(),
        user_agent: WebAuthnUtils.WebAuthnDetector.getUserAgent(),
        created_at: new Date().toISOString()
      };

      await supabase
        .from('webauthn_audit_logs')
        .insert(auditData);
    } catch (error) {
      console.error('Error logging audit event:', error);
      // Don't throw here to avoid breaking the main flow
    }
  }

  /**
   * Get client IP address (best effort)
   */
  private static async getClientIP(): Promise<string | null> {
    try {
      // This is a simple approach - in production you might want to use a service
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      return data.ip;
    } catch {
      return null;
    }
  }

  /**
   * Clean up expired challenges
   */
  static cleanupExpiredChallenges(): void {
    const now = new Date();
    for (const [key, challenge] of this.challenges.entries()) {
      if (!WebAuthnUtils.ChallengeManager.isValidChallenge(challenge)) {
        this.challenges.delete(key);
      }
    }
  }
}

// Clean up expired challenges every 5 minutes
setInterval(() => {
  WebAuthnService.cleanupExpiredChallenges();
}, 5 * 60 * 1000);

export default WebAuthnService;